import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../api/client';
import type { ICourseMembershipOption } from '../api/client';
import { useToast } from '../context/ToastContext';
import DataTable, { Column } from './DataTable';
import { Button, ConfirmModal } from './ui';
import Modal from './Modal';
import SearchableSelect from './SearchableSelect';
import MembershipForm, { IMembershipFormData } from './forms/MembershipForm';
import { seasonLabel } from '../utils/seasonLabel';
import '../App.css';
import { translateError } from '../utils/errorMessages';

interface CourseMembershipOptionsWidgetProps {
  courseId: number;
  /** The page-selected season whose options are managed. Null = none selected. */
  seasonId: number | null;
  /** Whether the selected season is editable (current/upcoming only). */
  canEdit: boolean;
  /** Selected season term — used only for the season label display. */
  termStart?: string | null;
  termEnd?: string | null;
  /** Bumped by the parent (e.g. after a renewal) to force a refetch. */
  refreshKey?: number;
  /** Called after a successful create / update / delete so the parent refetches. */
  onChanged: () => void;
}

/**
 * Lists the SELECTED season's membership OPTIONS (name + lowest term price + the
 * season label) and lets an admin add / edit / remove them via the season-nested
 * /api/courses/:id/seasons/:seasonId/membership-options endpoints.
 *
 * Self-fetches its options for the selected season (single uniform path for the
 * active and non-active seasons alike). Reuses the shared DataTable, Button,
 * Modal and ConfirmModal primitives and the standalone MembershipForm (in
 * course-option mode: courseId is context-fixed and the calendar config hidden).
 *
 * Editability follows the season lifecycle (`canEdit`): past/archived seasons are
 * view-only. The backend also guards writes — a non-editable season returns
 * `code: 'SEASON_NOT_EDITABLE'`, a duplicate name `code: 'MEMBERSHIP_NAME_TAKEN'`
 * (with a `suggestion`), and an unset term `code: 'COURSE_TERM_NOT_SET'`; each is
 * surfaced as a friendly toast.
 */
const CourseMembershipOptionsWidget: React.FC<CourseMembershipOptionsWidgetProps> = ({
  courseId,
  seasonId,
  canEdit,
  termStart,
  termEnd,
  refreshKey = 0,
  onChanged,
}) => {
  const { t, i18n } = useTranslation();
  const { addToast } = useToast();

  const [options, setOptions] = useState<ICourseMembershipOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  // A priced option is fiddly to re-enter; don't bin it on a stray Escape.
  const [formDirty, setFormDirty] = useState(false);
  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false);
  const [editing, setEditing] = useState<ICourseMembershipOption | null>(null);
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  // Attach-existing picker (offer an already-existing season-division membership
  // in this season rather than creating a new one).
  const [attachOpen, setAttachOpen] = useState(false);
  const [attachList, setAttachList] = useState<Array<{ _id: number; name: string }>>([]);
  const [attachLoading, setAttachLoading] = useState(false);
  const [attachSelected, setAttachSelected] = useState<number | null>(null);
  const [attaching, setAttaching] = useState(false);

  const season = useMemo(
    () => seasonLabel(termStart, termEnd, t, i18n.language),
    [termStart, termEnd, t, i18n.language],
  );

  const fetchOptions = useCallback(async () => {
    if (seasonId == null) {
      setOptions([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.courses.seasons.get(courseId, seasonId);
      setOptions(data.memberships ?? []);
    } catch (err: any) {
      setError(translateError(err, t));
      setOptions([]);
    } finally {
      setLoading(false);
    }
  }, [courseId, seasonId]);

  useEffect(() => {
    fetchOptions();
  }, [fetchOptions, refreshKey]);

  // Lowest priced option across a membership's derived price options (the term
  // price shown next to the option).
  const lowestPrice = (opt: ICourseMembershipOption): string => {
    const amounts = (opt.priceOptions ?? [])
      .map((p) => p.amount)
      .filter((n) => typeof n === 'number' && Number.isFinite(n));
    const fallback = opt.price != null ? parseFloat(String(opt.price)) : NaN;
    const min = amounts.length > 0 ? Math.min(...amounts) : fallback;
    if (!Number.isFinite(min)) return t('courses.noPricing');
    return t('courses.priceFrom', { price: min });
  };

  const openCreate = () => {
    setEditing(null);
    setModalOpen(true);
  };

  const openEdit = (opt: ICourseMembershipOption) => {
    setEditing(opt);
    setModalOpen(true);
  };

  const mapWriteError = (err: any): string => {
    const data = err.response?.data;
    const code = data?.code;
    if (code === 'SEASON_NOT_EDITABLE') return t('courses.membershipOptions.seasonNotEditable');
    if (code === 'MEMBERSHIP_NAME_TAKEN') {
      return t('courses.membershipOptions.nameTaken', {
        suggestion: data?.suggestion ?? '',
      });
    }
    if (code === 'COURSE_TERM_NOT_SET') return t('courses.termNotSet');
    return data?.message || err.message || t('common.error');
  };

  const handleSubmit = async (data: IMembershipFormData) => {
    if (seasonId == null) return;
    setSaving(true);
    try {
      if (editing) {
        await api.courses.seasons.membershipOptions.update(courseId, seasonId, editing._id, data);
        addToast(t('courses.membershipOptions.updated'), 'success');
      } else {
        await api.courses.seasons.membershipOptions.create(courseId, seasonId, data);
        addToast(t('courses.membershipOptions.created'), 'success');
      }
      setModalOpen(false);
      setEditing(null);
      setFormDirty(false);
      await fetchOptions();
      onChanged();
    } catch (err: any) {
      addToast(mapWriteError(err), 'error');
    } finally {
      setSaving(false);
    }
  };

  // Load existing season-division memberships NOT already offered in this season.
  const openAttach = async () => {
    setAttachSelected(null);
    setAttachOpen(true);
    setAttachLoading(true);
    try {
      const { data } = await api.memberships.list();
      const existingIds = new Set(options.map((o) => o._id));
      const attachable = (data as Array<{ _id: number; name: string; type: string }>)
        .filter((m) => m.type === 'season' && !existingIds.has(m._id))
        .map((m) => ({ _id: m._id, name: m.name }));
      setAttachList(attachable);
    } catch (err: any) {
      addToast(mapWriteError(err), 'error');
      setAttachList([]);
    } finally {
      setAttachLoading(false);
    }
  };

  const handleAttach = async () => {
    if (seasonId == null || attachSelected == null) return;
    setAttaching(true);
    try {
      await api.courses.seasons.membershipOptions.attach(courseId, seasonId, attachSelected);
      addToast(t('courses.membershipOptions.attached'), 'success');
      setAttachOpen(false);
      await fetchOptions();
      onChanged();
    } catch (err: any) {
      addToast(mapWriteError(err), 'error');
    } finally {
      setAttaching(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (deleteId == null || seasonId == null) return;
    setDeleteLoading(true);
    try {
      await api.courses.seasons.membershipOptions.delete(courseId, seasonId, deleteId);
      addToast(t('courses.membershipOptions.removed'), 'success');
      await fetchOptions();
      onChanged();
    } catch (err: any) {
      addToast(mapWriteError(err), 'error');
    } finally {
      setDeleteLoading(false);
      setDeleteId(null);
    }
  };

  const columns: Column<ICourseMembershipOption>[] = useMemo(() => {
    const cols: Column<ICourseMembershipOption>[] = [
      { header: t('memberships.name'), render: (opt) => opt.name },
      { header: t('courseSchedule.season'), render: () => season },
      { header: t('courses.pricing'), render: (opt) => lowestPrice(opt) },
    ];
    if (canEdit) {
      cols.push({
        header: t('common.actions'),
        render: (opt) => (
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <Button variant="secondary" size="sm" onClick={() => openEdit(opt)}>
              {t('common.edit')}
            </Button>
            <Button variant="danger" size="sm" onClick={() => setDeleteId(opt._id)}>
              {t('courses.membershipOptions.remove')}
            </Button>
          </div>
        ),
      });
    }
    return cols;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t, canEdit, season]);

  const editInitialData: Partial<IMembershipFormData> | undefined = useMemo(() => {
    if (!editing) return undefined;
    // Seed the persisted term price back into `yearlyPrice` so the form (and the
    // PUT that reuses buildOptionWriteValues on the submitted `yearlyPrice`) round-
    // trips it. Without this, a name-only edit rebuilds pricing from a default of 0
    // and clobbers the stored price. Mirrors the CREATE mapping (yearlyPrice → price).
    // For season options the DivisionSchemeField round-trips via `divisionScheme`.
    const termPrice = editing.price != null ? parseFloat(String(editing.price)) : NaN;
    return {
      name: editing.name,
      description: editing.description ?? '',
      type: editing.type,
      yearlyPrice: Number.isFinite(termPrice) ? termPrice : 0,
      entryCount: editing.entryCount,
      divisionScheme: editing.divisionScheme ?? undefined,
    };
  }, [editing]);

  const closeOptionModal = () => {
    setModalOpen(false);
    setEditing(null);
    setFormDirty(false);
    setDiscardConfirmOpen(false);
  };

  const guardOptionModalClose = (): boolean | void => {
    if (!formDirty) return;
    setDiscardConfirmOpen(true);
    return false;
  };

  return (
    <div className="form-section">
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '1rem',
          flexWrap: 'wrap',
          gap: '0.75rem',
        }}
      >
        <h4 className="form-section__title" style={{ margin: 0 }}>
          {t('courses.payments.title')} · {season}
        </h4>
        {canEdit && seasonId != null && (
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
            <Button variant="secondary" size="sm" onClick={openAttach}>
              {t('courses.membershipOptions.attachExisting')}
            </Button>
            <Button variant="primary" size="sm" onClick={openCreate}>
              {t('courses.membershipOptions.add')}
            </Button>
          </div>
        )}
      </div>

      <p className="form-help-text" style={{ marginTop: 0 }}>
        {t('courses.payments.help')}
      </p>

      {!canEdit && (
        <p className="form-help-text" style={{ marginTop: 0 }}>
          {t('courses.payments.viewOnly')}
        </p>
      )}

      {loading ? (
        <p>{t('common.loading')}</p>
      ) : error ? (
        <p className="error-message">
          {t('common.error')}: {error}
        </p>
      ) : options.length === 0 ? (
        <p style={{ color: 'var(--text-muted)' }}>{t('courses.membershipOptions.empty')}</p>
      ) : (
        <DataTable data={options} columns={columns} />
      )}

      <Modal
        isOpen={modalOpen}
        onClose={closeOptionModal}
        onRequestClose={guardOptionModalClose}
        title={
          editing
            ? t('courses.membershipOptions.editTitle')
            : t('courses.membershipOptions.addTitle')
        }
        size="xlarge"
      >
        <MembershipForm
          initialData={editInitialData}
          onDirtyChange={setFormDirty}
          onSubmit={handleSubmit}
          loading={saving}
          submitButtonText={t('common.save')}
          seasonBound={
            seasonId != null
              ? { id: seasonId, termStart: termStart ?? null, termEnd: termEnd ?? null }
              : undefined
          }
        />
      </Modal>

      <Modal
        isOpen={attachOpen}
        onClose={() => setAttachOpen(false)}
        title={t('courses.membershipOptions.attachExistingTitle')}
        size="medium"
      >
        {attachLoading ? (
          <p>{t('common.loading')}</p>
        ) : attachList.length === 0 ? (
          <p style={{ color: 'var(--text-muted)' }}>
            {t('courses.membershipOptions.attachExistingEmpty')}
          </p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            <SearchableSelect
              options={attachList}
              value={attachSelected}
              onChange={(v) => setAttachSelected(v == null ? null : Number(v))}
              placeholder={t('courses.membershipOptions.attachExisting')}
            />
            <Button
              variant="primary"
              onClick={handleAttach}
              loading={attaching}
              disabled={attachSelected == null}
            >
              {t('courses.membershipOptions.attach')}
            </Button>
          </div>
        )}
      </Modal>

      <ConfirmModal
        isOpen={discardConfirmOpen}
        onClose={() => setDiscardConfirmOpen(false)}
        onConfirm={closeOptionModal}
        title={t('unsavedChanges.title')}
        message={t('unsavedChanges.message')}
        confirmText={t('unsavedChanges.discard')}
        cancelText={t('unsavedChanges.keepEditing')}
        variant="danger"
      />

      <ConfirmModal
        isOpen={deleteId !== null}
        onClose={() => setDeleteId(null)}
        onConfirm={handleDeleteConfirm}
        title={t('courses.membershipOptions.remove')}
        message={t('courses.membershipOptions.removeConfirm')}
        confirmText={t('courses.membershipOptions.remove')}
        cancelText={t('common.cancel')}
        variant="danger"
        loading={deleteLoading}
      />
    </div>
  );
};

export default CourseMembershipOptionsWidget;
